/**
 * Feature Flags Service - Control de funcionalidades por ambiente y usuario
 */

export interface FeatureFlags {
  debugMode: boolean;
  dataMigration: boolean;
  authDiagnostics: boolean;
  firebaseConsoleHelper: boolean;
  smartPdfUploader: boolean;
  demoDataCreator: boolean;
  advancedCharts: boolean;
  betaFeatures: boolean;
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  debugMode: import.meta.env.VITE_DEBUG_MODE === 'true', // Controlado por variable de entorno
  dataMigration: false,
  authDiagnostics: false,
  firebaseConsoleHelper: false,
  smartPdfUploader: false,
  demoDataCreator: false,
  advancedCharts: false,
  betaFeatures: false,
};

export class FeatureFlagsService {
  private static readonly STORAGE_KEY = 'invirtiendo_feature_flags';
  private flags: FeatureFlags;
  private listeners: Set<(flags: FeatureFlags) => void> = new Set();

  constructor() {
    this.flags = this.loadFlags();
  }

  /**
   * Cargar flags desde localStorage o usar defaults
   */
  private loadFlags(): FeatureFlags {
    try {
      const stored = localStorage.getItem(FeatureFlagsService.STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Combinar con defaults para asegurar que todas las flags existan
        return { ...DEFAULT_FEATURE_FLAGS, ...parsed };
      }
    } catch (error) {
      console.warn('Error loading feature flags:', error);
    }
    return { ...DEFAULT_FEATURE_FLAGS };
  }

  /**
   * Guardar flags en localStorage
   */
  private saveFlags(): void {
    try {
      localStorage.setItem(FeatureFlagsService.STORAGE_KEY, JSON.stringify(this.flags));
      this.notifyListeners();
    } catch (error) {
      console.warn('Error saving feature flags:', error);
    }
  }

  /**
   * Obtener el estado de una flag específica
   * Para debugMode, la variable de entorno tiene precedencia
   */
  isEnabled(flag: keyof FeatureFlags): boolean {
    // La variable de entorno VITE_DEBUG_MODE tiene precedencia para debugMode
    if (flag === 'debugMode') {
      const envDebugMode = import.meta.env.VITE_DEBUG_MODE;
      if (envDebugMode !== undefined) {
        return envDebugMode === 'true';
      }
    }
    return this.flags[flag] ?? false;
  }

  /**
   * Obtener todas las flags
   */
  getAllFlags(): FeatureFlags {
    return { ...this.flags };
  }

  /**
   * Activar/desactivar una flag específica
   */
  setFlag(flag: keyof FeatureFlags, enabled: boolean): void {
    this.flags[flag] = enabled;
    this.saveFlags();
  }

  /**
   * Alternar el estado de una flag
   */
  toggleFlag(flag: keyof FeatureFlags): void {
    this.setFlag(flag, !this.flags[flag]);
  }

  /**
   * Resetear todas las flags a valores por defecto
   */
  resetToDefaults(): void {
    this.flags = { ...DEFAULT_FEATURE_FLAGS };
    this.saveFlags();
  }

  /**
   * Suscribirse a cambios en las flags
   */
  subscribe(listener: (flags: FeatureFlags) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notificar a todos los listeners sobre cambios
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.getAllFlags());
      } catch (error) {
        console.warn('Error notifying feature flag listener:', error);
      }
    });
  }

  /**
   * Obtener configuración para modo debug
   */
  getDebugConfig() {
    return {
      enabled: this.isEnabled('debugMode'),
      showMigration: this.isEnabled('dataMigration'),
      showAuthDiagnostics: this.isEnabled('authDiagnostics'),
      showFirebaseHelper: this.isEnabled('firebaseConsoleHelper'),
      showSmartUploader: this.isEnabled('smartPdfUploader'),
      showDemoCreator: this.isEnabled('demoDataCreator'),
    };
  }
}

// Instancia singleton
export const featureFlagsService = new FeatureFlagsService();